import { isFromContract, sameDate } from "../common";
import { type AvailabilityInfo } from "~/domains/product/api/pna/AvailabilityInfo";
import { type PackagingInformationEntry } from "~/domains/product/types/ProductData";
import { groupByFn, isNonNull } from "~/common/utils/reducers";
import { type EstimatedDeliveryDate } from "~/features/cart/cart/models/CartData";
import { StockLevel } from "~/domains/product/components/StockIndicator/StockIndicator";
import { type LineItemBuilder } from "../LineItemUpdate";
import { combineDeliveries, getStockData } from "./getStockData";
import { isPartialDeliveriesEnabled } from "../../features";
import dayjs from "dayjs";
import { addWorkingDays, TODAY } from "~/common/utils/dateUtils";
import { isReplenishment } from "~/utils/cartUpdating/delivery/stockDataUtils";
import { type StockData } from "~/utils/cartUpdating/delivery/stockDataTypes";

/**
 * Creates a list of edd updates for given line items
 * @param lineItems
 * @param availability
 */
export function refreshDeliveriesForProduct(lineItems: LineItemBuilder[], availability: AvailabilityInfo): void {
  // TODO: Cover contract items as well..
  lineItems = lineItems.filter((item) => !isFromContract(item));

  const byPackage = groupByFn(lineItems, (item) => item.get("packCode"));

  for (const [packCode, lineItems] of Object.entries(byPackage)) {
    const packageData = availability.packagingInformation.find((packageData) => packageData.packCode === packCode);
    if (!packageData) {
      continue;
    }
    refreshDeliveryForPackage(lineItems, packageData);
  }
  return;
}

/**
 * Creates a list of edd updates for given line items
 * @param lineItems line items belonging to a single package
 * @param packageData details of said package
 */
function refreshDeliveryForPackage(lineItems: LineItemBuilder[], packageData: PackagingInformationEntry): void {
  const stockData = getStockData(packageData);
  if (isPartialDeliveriesEnabled()) {
    lineItems.forEach((item) => {
      refreshPartialDeliveriesForLineItem(item, stockData);
    });
  } else {
    lineItems.forEach((item) => {
      refreshEddForLineItem(item, stockData);
    });
  }
}

/**
 * Creates a list of deliveries and inStock for given line item
 *
 * @param lineItem line item to update
 * @param stockData data on stock for this product and package. WARNING: is modified by this function
 */
function refreshPartialDeliveriesForLineItem(lineItem: LineItemBuilder, stockData: StockData): void {
  const offset = window.SCHA.offsetRDD;

  const partialDeliveries: StockData = [];

  let remainingQty = lineItem.get("quantity");
  const packSize = lineItem.get("packSize")!;

  if (Math.floor(remainingQty / packSize) * packSize !== remainingQty) {
    // Cannot calculate partial deliveries when we don't request a multiple of package size
    return;
  }

  while (stockData.length > 0 && remainingQty > 0) {
    const stockEntry = stockData[0];

    // End of logic - replenishment is added to partial deliveries
    if (stockEntry.type === "replenishment") {
      partialDeliveries.push({
        ...stockEntry,
        amount: remainingQty,
      });
      remainingQty = 0;
      break;
    }
    const stockAmount = stockEntry.amount;

    // Too few pieces to make one package -
    if (stockAmount < packSize) {
      if (stockData.length > 1) {
        // roll the remaining qty to next stock entry
        stockData.shift();
        if (stockData[0].type !== "replenishment") {
          stockData[0].amount += stockAmount;
        }
        continue;
      } else {
        // Can't make any more deliveries - too few pieces left and nothing more
        break;
      }
    }

    const fulfilledQty = Math.floor(Math.min(remainingQty, stockAmount) / packSize) * packSize;
    partialDeliveries.push({ ...stockEntry, amount: fulfilledQty });

    stockEntry.amount -= fulfilledQty;
    if (stockEntry.amount === 0) {
      // We have exhausted this entry
      stockData.shift();
    }
    remainingQty -= fulfilledQty;
  }

  if (remainingQty > 0) {
    // Sanity check - we normally should have a replenishment entry in the while loop
    partialDeliveries.push({
      amount: remainingQty,
      type: "replenishment",
      date: null,
    });
  }

  let stockLevel: StockLevel;
  if (!partialDeliveries.find((stockEntry) => stockEntry.type === "stock")) {
    stockLevel = StockLevel.NOT_IN_STOCK;
  } else if (partialDeliveries.length > 1) {
    stockLevel = StockLevel.PARTIAL_STOCK;
  } else {
    stockLevel = StockLevel.IN_STOCK;
  }
  if (lineItem.get("isInStock") !== stockLevel) {
    lineItem.set("isInStock", stockLevel);
  }

  const isManualRDD = lineItem.get("manuallySetRDD");
  if (!isManualRDD || !lineItem.get("deliveryDate")) {
    lineItem.set("partialDeliveries", partialDeliveries);

    const rdd = addWorkingDays(TODAY.toDate(), offset).toDate();

    lineItem.set("deliveryDate", rdd);

    if (partialDeliveries.length > 0 && partialDeliveries[0].date) {
      lineItem.set("edd", { type: "estimate", date: partialDeliveries[0].date });
    } else {
      lineItem.set("edd", null);
    }
  } else {
    // There is a manual RDD, first bunch all the deliveries before/equal to first shipping date
    const rdd = lineItem.get("deliveryDate")!;
    const firstShippingDate = addWorkingDays(rdd, -offset).toDate();
    const deliveriesToCombine = partialDeliveries.filter((stockEntry) =>
      dayjs(stockEntry.date).isSameOrBefore(firstShippingDate, "day"),
    );
    const remainingDeliveries = partialDeliveries.filter(
      (stockEntry) => !dayjs(stockEntry.date).isSameOrBefore(firstShippingDate, "day"),
    );

    const combinedDelivery = combineDeliveries(deliveriesToCombine);
    if (combinedDelivery) {
      combinedDelivery.date = firstShippingDate;
    }

    if (deliveriesToCombine.find(isReplenishment)) {
      // We have just one delivery, no partials
      if (combinedDelivery) {
        combinedDelivery.amount = lineItem.get("quantity");
      }
      lineItem.set("partialDeliveries", [combinedDelivery].filter(isNonNull));
      lineItem.set("edd", { type: "estimate", date: firstShippingDate });
    } else {
      // We have more deliveries after RDD
      const combinedPartialDeliveries = [combinedDelivery, ...remainingDeliveries].filter(isNonNull);
      const nonReplenishmentCombinedPartialDeliveries = combinedPartialDeliveries.filter(
        (partialDelivery) => !isReplenishment(partialDelivery),
      );

      lineItem.set("partialDeliveries", combinedPartialDeliveries);

      if (nonReplenishmentCombinedPartialDeliveries.length > 0 && nonReplenishmentCombinedPartialDeliveries[0]?.date) {
        lineItem.set("edd", { type: "estimate", date: nonReplenishmentCombinedPartialDeliveries[0].date });
      } else {
        lineItem.set("edd", null);
      }
    }
  }
}

/**
 * Creates updates for edd and inStock for given line item
 *
 * @param lineItem line item to update
 * @param stockData data on stock for this product and package. WARNING: is modified by this function
 */
function refreshEddForLineItem(lineItem: LineItemBuilder, stockData: StockData): void {
  let remainingQty = lineItem.get("quantity");

  let containsInStock = false;

  if (stockData.length === 0) {
    if (needsEddRefresh(lineItem, null)) {
      lineItem.set("edd", null);
    }
    if (lineItem.get("isInStock") !== StockLevel.NOT_IN_STOCK) {
      lineItem.set("isInStock", StockLevel.NOT_IN_STOCK);
    }
    return;
  }

  while (stockData.length > 0) {
    const stockEntry = stockData[0];

    if (stockEntry.type === "replenishment") {
      let edd: EstimatedDeliveryDate | null;
      if (stockEntry.date) {
        edd = {
          date: stockEntry.date,
          type: "replenishment",
        };
      } else {
        edd = null;
      }
      if (needsEddRefresh(lineItem, edd)) {
        lineItem.set("edd", edd);
      }
      if (lineItem.get("isInStock") !== StockLevel.NOT_IN_STOCK) {
        lineItem.set("isInStock", StockLevel.NOT_IN_STOCK);
      }
      break;
    }

    remainingQty = remainingQty - stockEntry.amount;
    containsInStock = containsInStock || stockEntry.type === "stock";

    let isFinished = false;

    if (remainingQty > 0) {
      stockData.shift();
      isFinished = stockData.length === 0;
    } else {
      // Fulfilled
      isFinished = true;
      if (remainingQty === 0) {
        stockData.shift();
      } else {
        stockEntry.amount = -remainingQty;
      }
    }

    if (isFinished) {
      // Finished

      let stockLevel: StockLevel;
      if (containsInStock) {
        // Fulfillment contains stock
        if (stockEntry.type === "stock" && remainingQty <= 0) {
          stockLevel = StockLevel.IN_STOCK;
        } else {
          stockLevel = StockLevel.PARTIAL_STOCK;
        }
      } else {
        stockLevel = StockLevel.NOT_IN_STOCK;
      }
      if (lineItem.get("isInStock") !== stockLevel) {
        lineItem.set("isInStock", stockLevel);
      }

      let edd: EstimatedDeliveryDate | null;
      if (remainingQty > 0 || !stockEntry.date) {
        edd = null;
      } else {
        edd = {
          date: stockEntry.date,
          type: "estimate",
        };
      }
      if (needsEddRefresh(lineItem, edd)) {
        lineItem.set("edd", edd);
      }

      break;
    }
  }
}

function needsEddRefresh(lineItem: LineItemBuilder, newEdd: EstimatedDeliveryDate | null) {
  const edd = lineItem.get("edd");
  if (edd) {
    return !newEdd || edd.type !== newEdd.type || !sameDate(edd.date, newEdd.date);
  } else {
    // !edd
    return !!newEdd;
  }
}
